import React from 'react';
import { G, styles } from './theme.js';

export function Btn({ v='ghost', sz='md', onClick, disabled, children, style={}, type='button', full=false }) {
  const p = sz==='sm'?'6px 12px':sz==='xs'?'3px 8px':'10px 20px';
  const f = sz==='sm'||sz==='xs'?12:14;
  const base = { padding:p, borderRadius:8, fontSize:f, cursor:disabled?'not-allowed':'pointer', fontWeight:600, border:'none', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6, opacity:disabled?.6:1, width:full?'100%':'auto', transition:'opacity .15s, transform .05s', ...style };
  const variants = {
    primary:{...base, background:G.accent, color:'#fff', boxShadow:'0 1px 0 rgba(255,255,255,.08) inset, 0 1px 3px rgba(255,106,61,.25)'},
    green: {...base, background:G.greenLight, color:G.green, border:`1px solid rgba(34,197,94,.3)`},
    danger:{...base, background:G.redLight, color:G.red, border:`1px solid rgba(239,68,68,.3)`},
    yellow:{...base, background:G.yellowLight, color:G.yellow, border:`1px solid rgba(234,179,8,.3)`},
    cyan:  {...base, background:G.cyanLight, color:G.cyan, border:`1px solid rgba(6,182,212,.3)`},
    outline:{...base, background:'transparent', border:`1px solid ${G.purpleBorder}`, color:G.purple},
    ghost: {...base, background:G.input, color:G.textMuted, border:`1px solid ${G.border}`},
  };
  return <button type={type} onClick={onClick} disabled={disabled} style={variants[v]||variants.ghost}>{children}</button>;
}

export function Badge({ c, bg, children }) {
  return <span style={{ background:bg, color:c, fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:3 }}>{children}</span>;
}
export function Dot({ c, pulse }) {
  return <span style={{ width:8, height:8, borderRadius:'50%', background:c, display:'inline-block', flexShrink:0, ...(pulse?{animation:'pulse 2s infinite'}:{}) }}/>;
}
export function Av({ name='?', sz=36 }) {
  const cols = [G.purple, G.cyan, G.blue, G.green, G.orange, G.pink];
  const col = cols[(name||'?').charCodeAt(0) % cols.length];
  return <div style={{ width:sz, height:sz, borderRadius:'50%', background:`${col}22`, border:`1px solid ${col}55`, color:col, fontSize:sz*.34, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
    {(name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
  </div>;
}
export function Spinner({ sz=20 }) {
  return <div style={{ width:sz, height:sz, border:`2px solid ${G.accentDim}`, borderTop:`2px solid ${G.accent}`, borderRadius:'50%', animation:'spin 1s linear infinite' }}/>;
}
export function Toast({ msg, type='success', onClose, action, onAction }) {
  if (!msg) return null;
  const bg = type==='error'?G.red:type==='warn'?G.yellow:G.green;
  return <div style={{ position:'fixed', bottom:24, right:24, background:bg, color:'#fff', padding:'14px 20px', borderRadius:12, fontSize:14, fontWeight:600, zIndex:9999, display:'flex', alignItems:'center', gap:12, boxShadow:'0 4px 24px rgba(0,0,0,.4)', maxWidth:440 }}>
    <span>{msg}</span>
    {action && <button onClick={() => { onAction && onAction(); onClose && onClose(); }}
      style={{ background:'rgba(255,255,255,.25)', border:'1px solid rgba(255,255,255,.4)', color:'#fff', padding:'5px 12px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
      {action}
    </button>}
    <span onClick={onClose} style={{ cursor:'pointer', opacity:.7, fontSize:18 }}>✕</span>
  </div>;
}
export function Modal({ open, onClose, children, width=720 }) {
  if (!open) return null;
  return <div style={styles.ovl} onClick={onClose}>
    <div data-modal-body onClick={e=>e.stopPropagation()} style={{ ...styles.card, width:'100%', maxWidth:`min(${width}px, calc(100vw - 24px))`, maxHeight:'92vh', overflow:'auto', padding:20 }}>
      {children}
    </div>
  </div>;
}
export function Tog({ on, onChange }) {
  return <div onClick={onChange} style={{ width:40, height:22, borderRadius:11, background:on?G.accent:G.input, border:`1px solid ${on?G.accent:G.border}`, cursor:'pointer', position:'relative', flexShrink:0 }}>
    <div style={{ position:'absolute', top:3, left:on?19:3, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .15s', boxShadow:'0 1px 3px rgba(0,0,0,.3)' }}/>
  </div>;
}
// Renders as <div> not <label> on purpose — composite widgets (ContactPicker,
// PriceBookPicker) live inside Fields, and <label> redirects clicks on inner
// non-form children to the first form control, breaking tap-to-pick on mobile.
export function Field({ label, children, hint }) {
  return <div style={{ display:'block', marginBottom:14 }}>
    <div style={{ fontSize:12, fontWeight:600, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize:12, color:G.textDim, marginTop:4 }}>{hint}</div>}
  </div>;
}
export function Input(props) { return <input {...props} style={{ ...styles.inp, ...(props.style||{}) }} />; }
export function Textarea(props) { return <textarea {...props} style={{ ...styles.inp, minHeight:80, resize:'vertical', ...(props.style||{}) }} />; }
export function Select({ children, ...p }) { return <select {...p} style={{ ...styles.inp, ...(p.style||{}) }}>{children}</select>; }
export function Section({ title, right, children, style={} }) {
  return <section style={{ marginBottom:24, ...style }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
      <h2 style={{ fontSize:18, fontWeight:700, margin:0 }}>{title}</h2>
      {right}
    </div>
    {children}
  </section>;
}

// EmptyState — replaces plain "No X yet" lines with coaching: icon, headline,
// one-sentence explanation, and a primary CTA button. The CTA is what most
// "no rows" screens were missing — users don't know what to do next.
export function EmptyState({ icon, title, body, ctaLabel, onCta, secondaryLabel, onSecondary, style={} }) {
  return <div style={{ padding:'64px 24px', textAlign:'center', ...style }}>
    {icon && <div style={{ fontSize:56, marginBottom:14, lineHeight:1 }}>{icon}</div>}
    {title && <div style={{ fontSize:18, fontWeight:700, color:G.text, marginBottom:6 }}>{title}</div>}
    {body && <div style={{ fontSize:14, color:G.textMuted, maxWidth:420, margin:'0 auto 18px' }}>{body}</div>}
    {(ctaLabel || secondaryLabel) && (
      <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap', marginTop:8 }}>
        {ctaLabel && <Btn v="primary" onClick={onCta}>{ctaLabel}</Btn>}
        {secondaryLabel && <Btn v="ghost" onClick={onSecondary}>{secondaryLabel}</Btn>}
      </div>
    )}
  </div>;
}
